import { NotificationMessagePayload } from "src/app/shared/store/notification/notification.actions";
import {
  Code,
  Severity,
} from "src/app/shared/store/notification/notification.state";
import {
  DEFAULT_RESET_VALUE,
  Exception,
  HttpRequestState,
} from "src/app/shared/store/shared/http-request-state";

import { ExceptionName } from "../../backend/shared/exceptions/exception.model";

import { NotificationErrorService } from "./notification-error-whitelist.service";

export class NotificationCoreService {
  static tryCreateHttpNotificationMessage(
    previousHttpRequestState: HttpRequestState,
    currentHttpRequestState: HttpRequestState,
  ) {
    const isErrorHandledExplicitly =
      NotificationErrorService.isErrorHandledExplicitly(
        currentHttpRequestState.error,
      );

    if (isErrorHandledExplicitly) {
      return;
    }

    // Previously there was no error, now there is
    if (!previousHttpRequestState.error && currentHttpRequestState.error) {
      return NotificationCoreService.createHttpErrorNotification(
        currentHttpRequestState.error,
      );
    }

    // Previously there was already an error but the error has now changed
    if (
      previousHttpRequestState.error &&
      currentHttpRequestState.error &&
      previousHttpRequestState.error.timestamp !==
        currentHttpRequestState.error.timestamp
    ) {
      return NotificationCoreService.createHttpErrorNotification(
        currentHttpRequestState.error,
      );
    }

    return;
  }

  static createHttpNotificationMessage(exception: Exception) {
    return NotificationCoreService.createHttpErrorNotification(exception);
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * There are 3 updates / phases happening  when updating a feature state which communicates with our backend:
   * ------------------------------------------------------------------------------------------------------------
   * 1. Phase - The store is being updated with the new state from the forms (isAfterSubmitPhase)
   * 2. Phase - The store's 'isBusy' flag is set to 'true' and the backend is being called (isBeforeBackendRequestPhase)
   * 3. Phase - The backend has responded and we updated our store with the response and set 'isBusy' to 'false' (isAfterBackendRequestPhase)
   *
   * Each of those phases triggers an update to our feature state and thus triggers the code for creating notifications.
   * To correctly identify those phases which are relevant for us, we have created the following helper methods.
   * This should help us to identify a single phase.
   */

  // 1. Phase - This is where the resets by the frontend happen
  static isAfterSubmitPhase(
    previousIsBusy: boolean | undefined,
    currentIsBusy: boolean | undefined,
  ) {
    return !previousIsBusy && !currentIsBusy;
  }

  // 2. Phase
  static isBeforeBackendRequestPhase(
    previousIsBusy: boolean | undefined,
    currentIsBusy: boolean | undefined,
  ) {
    return !previousIsBusy && currentIsBusy;
  }

  // 3. Phase - This is where the resets by the backend happen
  static isAfterBackendRequestPhase(
    previousIsBusy: boolean | undefined,
    currentIsBusy: boolean | undefined,
  ) {
    return previousIsBusy && !currentIsBusy;
  }

  // As each change to our store and the corresponding update to the backend trigger multiple updates in our feature state (explained above),
  // we would like to be sure that our notification is only created once instead of 3 times (once for each phase).
  // For that reason we check once if the property was reset by the frontend (isAfterSubmitPhase) and another time if it was reset by the backend (isAfterBackendRequestPhase)
  static hasValueBeenReset<T>(
    previousValue: T,
    currentValue: T,
    previousIsBusy: boolean | undefined,
    currentIsBusy: boolean | undefined,
  ) {
    // Frontend resets happen here
    const isAfterSubmitPhase = NotificationCoreService.isAfterSubmitPhase(
      previousIsBusy,
      currentIsBusy,
    );

    // Backend resets happen here
    const isAfterBackendRequestPhase =
      NotificationCoreService.isAfterBackendRequestPhase(
        previousIsBusy,
        currentIsBusy,
      );

    const hasBeenResetByTheFrontend =
      isAfterSubmitPhase &&
      currentValue === DEFAULT_RESET_VALUE &&
      previousValue !== null;

    const hasBeenResetByTheBackend =
      isAfterBackendRequestPhase &&
      currentValue === DEFAULT_RESET_VALUE &&
      previousValue !== null;

    return hasBeenResetByTheFrontend || hasBeenResetByTheBackend;
  }

  private static createHttpErrorNotification(exception: Exception) {
    const description =
      exception.innerException?.description ?? exception.description;

    const notification: NotificationMessagePayload = {
      severity: Severity.ERROR,
      code: Code.GENERAL_HTTP_PROBLEM,
      text: description,
      deleteAfterNumberOfMilliseconds:
        DEFAULT_NOTIFICATION_TIMEOUT_IN_MILLISECONDS,
    };

    if (exception.name === ExceptionName.BAD_REQUEST) {
      notification.text = NotificationCoreService.createValidationErrorMessage(
        exception.description,
        exception.data.validationErrors,
      );
    }

    return notification;
  }

  private static createValidationErrorMessage(
    exceptionDescription: string,
    validationErrors: string[],
  ) {
    let validationErrorMessage = `${exceptionDescription}:`;

    validationErrorMessage += validationErrors
      .map((validationError) => ` ${validationError}`)
      .join(".");

    return validationErrorMessage;
  }
}

export const DEFAULT_NOTIFICATION_TIMEOUT_IN_MILLISECONDS = 10_000;
